import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Icon, type IconName } from '@luacentrix/icons';
import { Button } from '@luacentrix/ui';
import { BrandLogo } from '../components/BrandMark';
import { DISCORD_INVITE } from '../components/PublicSite';
import {
  browserChecks,
  delay,
  initialChecks,
  mergeChecks,
  probeService,
  replaceCheck,
  runKeyDerivation,
  siteKeyOf,
  waitForDwell,
  GATEWAY_TURNSTILE_GRACE_MS,
  GATEWAY_VERIFY_TIMEOUT_MS,
  apiBaseUrlOf,
  type GatewayCheck,
  type GatewayCheckState,
} from './checks';
import { readClearance, writeClearance } from './clearance';
import { loadTurnstile } from './turnstile';
import { postVerification } from './verify';

const STATE_ICONS: Record<GatewayCheckState, IconName> = {
  pending: 'clock',
  running: 'loader',
  passed: 'check',
  warned: 'shield',
  failed: 'x',
  skipped: 'lock',
};

const STATE_LABELS: Record<GatewayCheckState, string> = {
  pending: 'Waiting',
  running: 'Running',
  passed: 'Passed',
  warned: 'Notice',
  failed: 'Failed',
  skipped: 'Skipped',
};

const PROBLEMS: Record<string, string> = {
  connection: 'This page is not served over HTTPS, so the browser check cannot run.',
  crypto: 'This browser does not expose WebCrypto, so the browser check cannot run.',
  automation: 'This browser reports an automation flag. Turn off automation and reload the page.',
};

const TURNSTILE_POLL_MS = 120;

export interface GatewayProps {
  children: ReactNode;
  siteKey?: string | null;
}

function summaryLine(checks: GatewayCheck[]): string {
  const relevant = checks.filter(check => check.state !== 'skipped');
  const failed = relevant.filter(check => check.state === 'failed').length;
  if (failed > 0) {
    return `${failed} check${failed === 1 ? '' : 's'} failed.`;
  }
  const settled = relevant.filter(
    check => check.state === 'passed' || check.state === 'warned',
  ).length;
  if (settled === 0) {
    return 'Starting the browser checks.';
  }
  if (settled < relevant.length) {
    return `Verified ${settled} of ${relevant.length} checks.`;
  }
  return 'Entering LuaCentrix.';
}

export function Gateway({
  children,
  siteKey = siteKeyOf(import.meta.env as unknown as Record<string, unknown>),
}: GatewayProps): ReactElement {
  const [cleared, setCleared] = useState<boolean>(() => readClearance() !== null);
  const [checks, setChecks] = useState<GatewayCheck[]>(() => initialChecks(siteKey !== null));
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [turnstileBroken, setTurnstileBroken] = useState(false);
  const [widgetKey, setWidgetKey] = useState(0);
  const startedAt = useRef(performance.now());
  const busyRef = useRef(false);
  const attemptRef = useRef(0);
  const brokenRef = useRef(false);
  const tokenRef = useRef<string | null>(null);
  const widgetRef = useRef<string | null>(null);
  const widgetStartedAt = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const baseUrl = useMemo(
    () => apiBaseUrlOf(import.meta.env as unknown as Record<string, unknown>),
    [],
  );

  useEffect(() => {
    if (cleared) {
      return;
    }
    const previous = document.title;
    document.title = 'LuaCentrix Gateway';
    return () => {
      document.title = previous;
    };
  }, [cleared]);

  useEffect(() => {
    if (cleared || siteKey === null) {
      return;
    }
    let cancelled = false;
    loadTurnstile()
      .then(api => {
        if (cancelled || containerRef.current === null || widgetRef.current !== null) {
          return;
        }
        widgetStartedAt.current = performance.now();
        widgetRef.current = api.render(containerRef.current, {
          sitekey: siteKey,
          theme: 'auto',
          size: 'flexible',
          callback: token => {
            tokenRef.current = token;
            setChecks(current =>
              replaceCheck(
                current,
                'turnstile',
                'passed',
                'Cloudflare Turnstile confirmed this browser.',
              ),
            );
          },
          'expired-callback': () => {
            tokenRef.current = null;
            setChecks(current =>
              replaceCheck(
                current,
                'turnstile',
                'warned',
                'The Turnstile token expired; complete the check again.',
              ),
            );
          },
          'error-callback': () => {
            tokenRef.current = null;
            brokenRef.current = true;
            setTurnstileBroken(true);
            setChecks(current =>
              replaceCheck(
                current,
                'turnstile',
                'warned',
                'Turnstile could not run; browser checks apply.',
              ),
            );
          },
        });
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        brokenRef.current = true;
        setTurnstileBroken(true);
        setChecks(current =>
          replaceCheck(
            current,
            'turnstile',
            'warned',
            'Turnstile could not load; browser checks apply.',
          ),
        );
      });
    return () => {
      cancelled = true;
      const api = window.turnstile;
      const widget = widgetRef.current;
      if (api !== undefined && widget !== null) {
        api.remove(widget);
      }
      widgetRef.current = null;
      widgetStartedAt.current = null;
      tokenRef.current = null;
    };
  }, [cleared, siteKey, widgetKey]);

  const settleTurnstile = async (
    stale: () => boolean,
  ): Promise<'continue' | 'rejected' | 'stale'> => {
    if (siteKey === null || brokenRef.current) {
      return 'continue';
    }
    const deadline = (widgetStartedAt.current ?? performance.now()) + GATEWAY_TURNSTILE_GRACE_MS;
    let token = tokenRef.current;
    while (token === null) {
      if (stale()) {
        return 'stale';
      }
      if (performance.now() >= deadline) {
        brokenRef.current = true;
        setTurnstileBroken(true);
        setChecks(current =>
          replaceCheck(
            current,
            'turnstile',
            'warned',
            'Turnstile did not finish in time; browser checks apply.',
          ),
        );
        return 'continue';
      }
      await delay(TURNSTILE_POLL_MS);
      token = tokenRef.current;
    }
    const first = await postVerification(baseUrl, token, GATEWAY_VERIFY_TIMEOUT_MS);
    if (stale()) {
      return 'stale';
    }
    const settled =
      first === 'unavailable'
        ? await postVerification(baseUrl, token, GATEWAY_VERIFY_TIMEOUT_MS)
        : first;
    if (stale()) {
      return 'stale';
    }
    if (settled === 'verified') {
      setChecks(current =>
        replaceCheck(current, 'turnstile', 'passed', 'Cloudflare Turnstile confirmed the token.'),
      );
      return 'continue';
    }
    if (settled === 'not_configured' || settled === 'unavailable') {
      brokenRef.current = true;
      setTurnstileBroken(true);
      setChecks(current =>
        replaceCheck(
          current,
          'turnstile',
          'warned',
          settled === 'not_configured'
            ? 'Turnstile is not configured on the server; browser checks apply.'
            : 'The verification service did not answer; browser checks apply.',
        ),
      );
      return 'continue';
    }
    const api = window.turnstile;
    const widget = widgetRef.current;
    if (api !== undefined && widget !== null) {
      api.reset(widget);
    }
    tokenRef.current = null;
    setChecks(current =>
      replaceCheck(current, 'turnstile', 'failed', 'Turnstile rejected the token.'),
    );
    return 'rejected';
  };

  const run = async (): Promise<void> => {
    if (busyRef.current) {
      return;
    }
    const attempt = attemptRef.current + 1;
    attemptRef.current = attempt;
    const stale = (): boolean => attemptRef.current !== attempt;
    busyRef.current = true;
    setBusy(true);
    setProblem(null);

    const signals = browserChecks();
    setChecks(current => mergeChecks(current, signals));
    const blocked = signals.find(check => check.required && check.state === 'failed');
    if (blocked !== undefined) {
      setProblem(PROBLEMS[blocked.id] ?? 'A required browser check failed.');
      busyRef.current = false;
      setBusy(false);
      return;
    }

    setChecks(current =>
      replaceCheck(current, 'timing', 'running', 'Measuring the interaction delay.'),
    );
    const dwell = await waitForDwell(startedAt.current);
    if (stale()) {
      return;
    }
    setChecks(current =>
      replaceCheck(current, 'timing', 'passed', `Interaction recorded after ${dwell} ms.`),
    );

    setChecks(current =>
      replaceCheck(current, 'service', 'running', 'Contacting the LuaCentrix API.'),
    );
    const reachable = await probeService(baseUrl);
    if (stale()) {
      return;
    }
    setChecks(current =>
      replaceCheck(
        current,
        'service',
        reachable ? 'passed' : 'warned',
        reachable
          ? 'The API answered the health probe.'
          : 'The API did not answer; the site can still load.',
      ),
    );

    const turnstile = await settleTurnstile(stale);
    if (turnstile === 'stale') {
      return;
    }
    if (turnstile === 'rejected') {
      setProblem('Cloudflare Turnstile rejected this browser. Complete the check again.');
      busyRef.current = false;
      setBusy(false);
      return;
    }

    setChecks(current =>
      replaceCheck(current, 'work', 'running', 'Deriving the browser key.'),
    );
    try {
      const millis = await runKeyDerivation();
      if (stale()) {
        return;
      }
      setChecks(current =>
        replaceCheck(current, 'work', 'passed', `Key derivation completed in ${millis} ms.`),
      );
    } catch {
      setChecks(current =>
        replaceCheck(current, 'work', 'failed', 'The browser could not complete key derivation.'),
      );
      setProblem('The browser could not complete key derivation. Reload the page and try again.');
      busyRef.current = false;
      setBusy(false);
      return;
    }

    writeClearance();
    busyRef.current = false;
    setBusy(false);
    setCleared(true);
  };

  useEffect(() => {
    if (cleared) {
      return;
    }
    void run();
    return () => {
      attemptRef.current += 1;
      busyRef.current = false;
    };
  }, [cleared, baseUrl, siteKey]);

  const retry = (): void => {
    attemptRef.current += 1;
    busyRef.current = false;
    brokenRef.current = false;
    tokenRef.current = null;
    startedAt.current = performance.now();
    setBusy(false);
    setTurnstileBroken(false);
    setWidgetKey(key => key + 1);
    setChecks(initialChecks(siteKey !== null));
    void run();
  };

  if (cleared) {
    return <>{children}</>;
  }

  return (
    <div className="gateway">
      <header className="gateway__bar">
        <span className="gateway__brand">
          <BrandLogo size="sm" />
          <span className="gateway__brand-name">LuaCentrix</span>
        </span>
        <span className="gateway__bar-meta">by Centrix Hub</span>
      </header>

      <main className="gateway__main">
        <section className="gateway__panel" aria-labelledby="gateway-title">
          <p className="gateway__eyebrow">Automatic check</p>
          <h1 className="gateway__title" id="gateway-title">
            LuaCentrix Gateway
          </h1>
          <p className="gateway__lede">
            These checks run on their own when the page opens — there is nothing to press. A passing
            check keeps this browser cleared for 12 hours.
          </p>

          <ul className="gateway__checks" aria-live="polite">
            {checks.map(check => (
              <li className="gateway__check" data-state={check.state} key={check.id}>
                <span className="gateway__check-icon" aria-hidden="true">
                  <Icon
                    name={STATE_ICONS[check.state]}
                    size="sm"
                    className={check.state === 'running' ? 'lc-spinner' : undefined}
                  />
                </span>
                <span className="gateway__check-body">
                  <span className="gateway__check-label">{check.label}</span>
                  <span className="gateway__check-detail">{check.detail}</span>
                </span>
                <span className="gateway__check-state">{STATE_LABELS[check.state]}</span>
              </li>
            ))}
          </ul>

          {siteKey === null || turnstileBroken ? null : (
            <div className="gateway__turnstile" ref={containerRef} />
          )}

          <p className="gateway__summary" role="status">
            {summaryLine(checks)}
          </p>

          {problem === null ? null : (
            <>
              <p className="gateway__problem" role="alert">
                {problem}
              </p>
              <div className="gateway__actions">
                <Button variant="primary" icon="shield" loading={busy} onClick={retry}>
                  Re-run checks
                </Button>
                <a
                  className="gateway__support"
                  href={DISCORD_INVITE}
                  rel="noreferrer"
                  target="_blank"
                >
                  Trouble getting in? Join Discord
                </a>
              </div>
            </>
          )}

          {problem === null ? null : (
            <p className="gateway__footnote">
              Accounts, license keys and builds stay checked by the server on every request.
            </p>
          )}
        </section>
      </main>

      <footer className="gateway__footer">
        <span>LuaCentrix by Centrix Hub</span>
        <span className="gateway__footer-meta">Pass kept for 12 hours per browser.</span>
      </footer>
    </div>
  );
}
